// Fehlertexte für TTS/STS-Generierung, die ein Talent versteht (Audit
// 2026-09-24, M13). Vorher landete die rohe Server-Message im Toast — bei
// ElevenLabs-Fehlern ein JSON-Blob („{"detail":{"status":"too_many_…“), bei
// Netzwerkfehlern „Failed to fetch“. Reine Funktion, damit sie testbar ist;
// die Views hängen nur noch `describeGenerationError(err)` in den Toast.

export interface GenerationErrorLike {
  name?: string;
  status?: number;
  message?: string;
}

/** Nutzer-Abbruch (Asset-Wechsel, Cancel) — kein Toast. */
export function isGenerationAbort(err: unknown): boolean {
  return !!err && typeof err === 'object' && (err as GenerationErrorLike).name === 'AbortError';
}

/** Client-Timeout aus `generateTts`/`generateSts`. */
export function isGenerationTimeout(err: unknown): boolean {
  return !!err && typeof err === 'object' && (err as GenerationErrorLike).name === 'TimeoutError';
}

/** Sieht die Server-Message nach Roh-JSON oder Upstream-Interna aus? Dann nie anzeigen. */
function looksTechnical(msg: string): boolean {
  const m = msg.trim();
  return m.startsWith('{') || m.startsWith('[') || /elevenlabs|xi-api|detail|traceback|internal_error/i.test(m) || m.length > 140;
}

function codeOf(err: unknown): string | null {
  const body = err && typeof err === 'object' ? (err as { body?: unknown }).body : undefined;
  const code = body && typeof body === 'object' ? (body as { code?: unknown }).code : undefined;
  return typeof code === 'string' ? code : null;
}

export function describeGenerationError(err: unknown): string {
  if (isGenerationAbort(err)) return 'Generation cancelled.';
  if (isGenerationTimeout(err)) return 'The voice server took too long to respond. Please try again.';
  const e = (err && typeof err === 'object' ? err : {}) as GenerationErrorLike;
  const status = typeof e.status === 'number' ? e.status : undefined;
  const msg = typeof e.message === 'string' ? e.message : '';
  if (err instanceof TypeError || status === 0) {
    return 'No connection to the server. Please check your internet connection and try again.';
  }
  switch (status) {
    case 429:
      // Eigene 429 mit `code` (Integrations-Review 2026-09-24): kein Stau,
      // sondern eine Grenze — „busy, try again" wäre hier irreführend.
      if (codeOf(err) === 'quota') {
        return 'This project has reached its limit for voice generations for now. Please wait about 10 minutes and try again.';
      }
      if (codeOf(err) === 'pw_locked') {
        return 'Too many wrong password attempts. Access is paused for 15 minutes — please try again later.';
      }
      return 'The voice server is busy right now. Please wait a moment and try again.';
    case 401:
      return 'Your project password is no longer valid. Please reload the page and enter it again.';
    case 403:
      return 'This voice or project is not available. Please contact TRO.';
    case 413:
      return 'The text or recording is too long for a single request. Please split it into smaller blocks.';
    case 502:
    case 503:
    case 504:
      return 'The voice service is temporarily unavailable. Please try again in a moment.';
    case 400:
      return msg && !looksTechnical(msg) ? `The request was rejected: ${msg}` : 'The request was rejected. Please check the text and try again.';
    default:
      if (msg && !looksTechnical(msg)) return `Generation failed: ${msg}`;
      return status ? `Generation failed (error ${status}). Please try again.` : 'Generation failed. Please try again.';
  }
}
